import PartiesCard from './partiesCard';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFetchMatterParties } from '../../hooks/useFetchMatterParties';
import { PAYER_ROLE_ID } from '../../constants/conflictSearch';
import { useEffect, useMemo, useState } from 'react';
import NoData from '../dashboard/noData';
import { ProfileUserEmpty } from '../icons';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import { IMatterParties } from '../../interface/matterParties';
import { useTranslation } from 'react-i18next';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useQueryClient } from '@tanstack/react-query';
import { ICustomerType } from '../../constants/customer';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const PartiesListing = ({
  isBillable = false,
  type,
  noDataHeader,
}: {
  isBillable?: boolean;
  type: string;
  noDataHeader?: string;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { push } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('partiesSearch') || '';
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const {
    refetch,
    isFetching,
    isLoading: isPartiesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: paginatedPartiesData,
  } = useFetchMatterParties({
    id,
    type,
    relations: [
      'company:name,companyNumber',
      'privatePerson:fullName,privatePersonNumber',
      'roles',
      'roles.matterPartyRole',
    ],
    ...(isBillable ? { 'filter.matterPartyRoleId': `${PAYER_ROLE_ID}` } : {}),
    search: searchValue,
    fields: ['sharePercentage'],
  });

  const partiesSortedByPayerPercentage = useMemo(() => {
    return [...paginatedPartiesData].sort((a, b) => b.sharePercentage - a.sharePercentage);
  }, [paginatedPartiesData]);

  const handleGoToCustomer = (parties: IMatterParties) => {
    if (!parties?.company && !parties?.privatePerson) return;
    push(ROUTES.CUSTOMER_LISTING + `/${parties?.company?.id ?? parties?.privatePerson?.id}`);
  };
  useEffect(() => {
    setSearchParams({
      partiesSearch: searchValue,
    });
  }, [searchValue]);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && paginatedPartiesData?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="parties-listing"
      header={
        <SearchInputWithFilters
          searchTitle={noDataHeader ? t('matter:searchPayers') : t('matter:searchParties')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          searchValue={searchValue}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['matter-parties-list'],
            });
            return refetch();
          }}
        />
      }
    >
      {Object.entries(partiesSortedByPayerPercentage).length > 0 &&
        partiesSortedByPayerPercentage?.map((item: IMatterParties) => {
          return (
            <PartiesCard
              data-testid={`parties-card`}
              key={item?.id}
              id={item?.id}
              customerName={item?.company?.name ?? item?.privatePerson?.fullName ?? '-'}
              roles={item?.roles ?? []}
              customerType={item?.company ? ICustomerType.COMPANY : ICustomerType.PRIVATE_PERSON}
              sharePercentage={item?.sharePercentage?.toString() ?? ''}
              onPress={() => handleGoToCustomer(item)}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!paginatedPartiesData ||
        (Object.entries(paginatedPartiesData).length === 0 && !isFetching)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={noDataHeader ?? t('matter:noParties')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<ProfileUserEmpty />}
          />
        </div>
      )}
      {isPartiesLoading ? <SkeletonCardList rows={[1, 1]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default PartiesListing;
